import React, { useState, useEffect, useRef } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  LinearProgress,
  CircularProgress,
  Chip,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Autocomplete, // Adicionado
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  HourglassEmpty as PendingIcon,
  Timer as TimerIcon,
  Article as ArticleIcon,
  Link as LinkIcon,
  CheckCircle as SuccessIcon,
  Cancel as ErrorIcon,
  YouTube as YouTubeIcon,
  Language as WebIcon,
  Sync as SyncIcon,
  Remove as MinimizeIcon,
  FindInPage as DorkIcon
} from '@mui/icons-material';
import { scraperConfigAPI } from '../../../../services/scraperConfigAPI';
import api, { getTags, createTag } from '../../../../services/api'; 
import {
  operationalElevation,
  operationalFeedback,
  operationalFontSizes,
  operationalMotion,
  operationalRadii,
  operationalScrollbars,
  operationalSizing,
  operationalStatusColors
} from '../../../../design-system/tokens';
import ExistingArticlesModal from '../ExistingArticlesModal/ExistingArticlesModal';
import ArticleEditModal from '../ArticleEditModal';
import { useAuth } from '../../../../context/AuthContext';
import { useBrand } from '../../../../context/BrandContext';
import DorkDiscoveryTab from './DorkDiscoveryTab';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import TagChip from '../../../atoms/TagChip';
import RadarToChartAnimation from './RadarToChartAnimation';
import { ReactComponent as MensureLogo } from '../../../../assets/logo.svg';

// Ícone customizado do Twitter/X
const TwitterIcon = ({ sx }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Ícone customizado do Instagram
const InstagramIcon = ({ sx }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

// Ícone customizado do TikTok
const TikTokIcon = ({ sx }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
  </svg>
);

// Ícone customizado do Facebook
const FacebookIcon = ({ sx }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Logo oficial do Threads
const ThreadsIcon = ({ sx }) => (
  <svg viewBox="0 0 192 192" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" />
  </svg>
);

// Logo do Spotify
const SpotifyIcon = ({ sx }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={sx}>
    <path fill="currentColor" d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────

const BackgroundScrapingModal = ({ open, onClose, onJobStarted, onRestore }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const { activeBrandData } = useBrand();
  const surfaceSx = {
    borderRadius: `${operationalRadii.surface}px`,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: operationalElevation.none
  };
  const buttonSx = {
    minHeight: operationalSizing.compact,
    borderRadius: `${operationalRadii.control}px`,
    fontWeight: 650,
    textTransform: 'none',
    boxShadow: operationalElevation.none,
    '&:hover': {
      boxShadow: operationalElevation.none
    }
  };
  const progressSx = {
    borderRadius: operationalRadii.pill,
    bgcolor: 'action.selected',
    '& .MuiLinearProgress-bar': {
      borderRadius: operationalRadii.pill
    }
  };
  const statusPillSx = () => {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.25,
      py: 0.55,
      borderRadius: operationalRadii.pill,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      color: 'text.secondary',
      fontWeight: 650
    };
  };
  const warningAlertSx = {
    mb: 2,
    bgcolor: 'background.paper',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'warning.main',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid`,
    borderLeftColor: 'warning.main',
    borderRadius: `${operationalFeedback.alert.radius}px`,
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: 'warning.main'
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
      pt: 0
    },
    '& .MuiButton-root': {
      bgcolor: 'background.default',
      borderColor: 'divider',
      color: 'text.primary',
      boxShadow: operationalElevation.none,
      borderRadius: `${operationalRadii.control}px`,
      '&:hover': {
        bgcolor: 'action.hover',
        borderColor: 'text.secondary',
        boxShadow: operationalElevation.none
      }
    }
  };
  const infoAlertSx = {
    mb: 2,
    bgcolor: 'background.paper',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'info.main',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid`,
    borderLeftColor: 'info.main',
    borderRadius: `${operationalFeedback.alert.radius}px`,
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: 'info.main'
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
      pt: 0
    }
  };
  const errorAlertSx = {
    mb: 2,
    bgcolor: 'background.paper',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'error.main',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid`,
    borderLeftColor: 'error.main',
    borderRadius: `${operationalFeedback.alert.radius}px`,
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: operationalStatusColors.error
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
      pt: 0
    }
  };

  // Estados existentes
  const [urls, setUrls] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAddingUrls, setIsAddingUrls] = useState(false); // loading do botão +
  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [clientDuplicates, setClientDuplicates] = useState([]);
  const [showClientDuplicateAlert, setShowClientDuplicateAlert] = useState(false);
  const [showExistingArticlesModal, setShowExistingArticlesModal] = useState(false);
  const [existingArticles, setExistingArticles] = useState([]);
  // URLs ignoradas por serem near-duplicatas (identificadas ao iniciar o scraping)
  const [skippedDuplicates, setSkippedDuplicates] = useState([]);
  // Pares de URLs suspeitas (mesmo domínio, slugs muito parecidos — possíveis typos)
  const [suspiciousUrls, setSuspiciousUrls] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [trackingPeriod, setTrackingPeriod] = useState('48h');
  const [selectedMonitors, setSelectedMonitors] = useState([]);

  // Sincronizar selectedMonitors quando a brand muda ou abre o modal
  useEffect(() => {
    if (open && activeBrandData?.trackingMonitorNames) {
      setSelectedMonitors([...activeBrandData.trackingMonitorNames]);
    }
  }, [open, activeBrandData?.trackingMonitorNames]);

  // Se o componente superior (parent) comandar a abertura (ex: clicando em "+ Artigos"), 
  // precisamos garantir que o modal não fique restrito pelo mnimized.
  useEffect(() => {
    if (open && isMinimized) {
      setIsMinimized(false);
    }
  }, [open, isMinimized]);

  // Novos estados para criação manual
  const [tabValue, setTabValue] = useState(0); // 0 = Scraping, 1 = Manual
  const [createArticleModalOpen, setCreateArticleModalOpen] = useState(false);
  const [mainEditModalOpen, setMainEditModalOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(null);

  // Estado para tags
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Carregar tags ao abrir o modal
  useEffect(() => {
    if (open) {
      loadTags();
    } else {
      // Limpar seleção ao fechar
      setSelectedTags([]);
    }
  }, [open]);

  const loadTags = async () => {
    try {
      setLoadingTags(true);
      const response = await getTags();
      // A API retorna { success: true, data: [...] }
      // O axios retorna { data: { success: true, data: [...] } }
      // Se api.js não fizer unwrap, precisamos de response.data.data
      // Se api.js fizer unwrap (retornar response.data), precisamos de response.data
      // Verificando api.js, ele retorna api.get(), que é o axios promise.
      // Então response é o axios response. response.data é o body.
      setAvailableTags(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleCreateNewTag = async (tagName) => {
    try {
      setLoadingTags(true);
      const response = await createTag({ name: tagName, color: '#2196f3' });
      const newTag = response.data.data;
      setAvailableTags(prev => [...prev, newTag]);
      setSelectedTags(prev => [...prev, newTag]);
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      setError('Erro ao criar tag: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTags(false);
    }
  };

  // Estados para cronômetro
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [finalElapsedTime, setFinalElapsedTime] = useState(0);
  const [isTimerStopped, setIsTimerStopped] = useState(false);
  const isPollingStopped = useRef(false);

  // Função para formatar tempo em minutos:segundos:milissegundos
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const millis = Math.floor((ms % 1000) / 10); // 00 a 99 (centésimos para visual mais limpo)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${millis.toString().padStart(2, '0')}`;
  };

  // Calcular tempo estimado restante (ETA)
  const calculateETA = () => {
    if (!jobStatus || !elapsedTime || jobStatus.completed === 0) return null;

    const avgTimePerItem = elapsedTime / jobStatus.completed;
    const remainingItems = jobStatus.total - jobStatus.completed;
    const estimatedRemainingTime = Math.floor(avgTimePerItem * remainingItems);

    return estimatedRemainingTime;
  };

  // Parâmetros de rastreamento (UTM, Instagram, FB, etc) que devem ser ignorados
  // Sincronizado com backend/utils/urlCleaner.js
  const TRACKING_PARAMS = new Set([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'utm_id', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic',
    'igsh', 'igshid', 'ig_rid', 'ig_web_copy_link',
    'fbclid', 'fb_action_ids', 'fb_action_types', 'fb_source', 'fb_ref',
    'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid',
    'ref_src', 'ref_url', 's', 't',
    'mc_cid', 'mc_eid', '_ga', '_gl', 'ref', 'referrer',
    'amp', 'amp_js_v', 'amp_gsa', 'amp_rfe'
  ]);

  // Normaliza URL para comparação: remove /amp, trailing slash e params de rastreamento
  // MAS mantém parâmetros que identificam o conteúdo (ex: idnot=, id=, p=)
  const normalizeUrlForComparison = (url) => {
    if (!url) return '';

    try {
      const u = new URL(url.trim());

      // 1. Lowercase hostname para consistência
      const hostname = u.hostname.toLowerCase();

      // 2. Remover apenas parâmetros de rastreamento (case-insensitive para as chaves)
      const keysToDelete = [];
      u.searchParams.forEach((value, key) => {
        if (TRACKING_PARAMS.has(key.toLowerCase())) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => u.searchParams.delete(key));
      u.searchParams.sort();

      // 3. Normalizar path (remover /amp e remover trailing slash)
      // IMPORTANTE: NÃO usar .toLowerCase() pois IDs (YouTube, Instagram) são case-sensitive
      let path = u.pathname;
      path = path.replace(/\/amp\/?$/i, ''); // remover sufixo /amp (insensitivamente)

      // Remover todas as barras finais (trailing slashes)
      path = path.replace(/\/+$/, '');
      if (!path) path = '/'; // Garantir que a raiz seja pelo menos /

      // 4. Reconstrução manual para garantir consistência entre browsers
      let search = u.search;
      if (search === '?') search = '';

      return `${u.protocol}//${hostname}${path}${search}`;
    } catch (e) {
      // Fallback robusto para URLs inválidas
      let normalized = url.toLowerCase().trim();
      normalized = normalized.replace(/\/amp\/?$/, '');
      return normalized.replace(/\/+$/, '');
    }
  };

  // Função para detectar duplicatas na lista de URLs (inclui near-duplicatas como /amp)
  const detectClientDuplicates = (urlList) => {
    const duplicates = [];
    const seenNormalized = new Map(); // normalized URL -> { index, url } da primeira ocorrência

    urlList.forEach((url, index) => {
      const normalized = normalizeUrlForComparison(url.trim());
      if (seenNormalized.has(normalized)) {
        const original = seenNormalized.get(normalized);
        duplicates.push({
          url: url.trim(),             // URL duplicada (a ser removida)
          originalUrl: original.url,  // URL original (a ser mantida)
          index: index,
          originalIndex: original.index
        });
      } else {
        seenNormalized.set(normalized, { index, url: url.trim() });
      }
    });

    return duplicates;
  };

  // Função para remover duplicatas automaticamente, mantendo a versão sem /amp
  const removeClientDuplicates = () => {
    const seen = new Map(); // normalized -> melhor URL

    urls.forEach(url => {
      const normalized = normalizeUrlForComparison(url.trim());
      if (!seen.has(normalized)) {
        seen.set(normalized, url.trim());
      } else {
        // Prefere a versão sem /amp (mais canônica)
        const existing = seen.get(normalized);
        const isExistingAmp = /\/amp\/?$/.test(existing.toLowerCase());
        const isCurrentAmp = /\/amp\/?$/.test(url.toLowerCase());
        if (isExistingAmp && !isCurrentAmp) {
          seen.set(normalized, url.trim());
        }
      }
    });

    setUrls([...seen.values()]);
    setClientDuplicates([]);
    setShowClientDuplicateAlert(false);
  };

  // Distância de Levenshtein (termina cedo se ultrapassar o limite)
  const levenshteinDistance = (a, b, maxDist = 3) => {
    if (Math.abs(a.length - b.length) > maxDist) return maxDist + 1;
    const dp = Array.from({ length: a.length + 1 }, (_, i) => i);
    for (let j = 1; j <= b.length; j++) {
      let prev = dp[0];
      dp[0] = j;
      let rowMin = dp[0];
      for (let i = 1; i <= a.length; i++) {
        const temp = dp[i];
        dp[i] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[i], dp[i - 1]);
        prev = temp;
        rowMin = Math.min(rowMin, dp[i]);
      }
      if (rowMin > maxDist) return maxDist + 1;
    }
    return dp[a.length];
  };

  // Detecta URLs do mesmo domínio com slugs muito parecidos (possíveis typos)
  const detectSuspiciousUrls = (urlList) => {
    const suspicious = [];
    const byDomain = {};

    urlList.forEach((url) => {
      try {
        const hostname = new URL(url).hostname;
        if (!byDomain[hostname]) byDomain[hostname] = [];
        byDomain[hostname].push(url);
      } catch (e) { /* URL inválida */ }
    });

    Object.values(byDomain).forEach((domainUrls) => {
      if (domainUrls.length < 2) return;
      for (let i = 0; i < domainUrls.length; i++) {
        for (let j = i + 1; j < domainUrls.length; j++) {
          const urlA = domainUrls[i];
          const urlB = domainUrls[j];

          // Usar URL normalizada (sem duplicatas exatas já capturadas)
          const normA = normalizeUrlForComparison(urlA);
          const normB = normalizeUrlForComparison(urlB);
          if (normA === normB) continue; // já capturado como duplicata exata

          // Extrair slug (último segmento do path)
          const slugA = normA.split('/').filter(Boolean).pop() || '';
          const slugB = normB.split('/').filter(Boolean).pop() || '';

          // Só comparar slugs suficientemente longos (evita falsos positivos)
          if (slugA.length < 15 || slugB.length < 15) continue;

          const dist = levenshteinDistance(slugA, slugB, 2);
          if (dist > 0 && dist <= 2) {
            // Evitar adicionar o mesmo par duas vezes
            const alreadyAdded = suspicious.some(
              p => (p.urlA === urlA && p.urlB === urlB) || (p.urlA === urlB && p.urlB === urlA)
            );
            if (!alreadyAdded) {
              suspicious.push({ urlA, urlB, dist });
            }
          }
        }
      }
    });

    return suspicious;
  };

  // Cronômetro - atualiza a cada segundo quando há um job ativo e não foi parado
  useEffect(() => {
    let interval;

    if (jobId && startTime && !isTimerStopped) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - startTime; // Em milissegundos
        setElapsedTime(elapsed);
      }, 50); // 50ms para 20 frames por segundo
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [jobId, startTime, isTimerStopped]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    // Reset da flag de polling parado
    isPollingStopped.current = false;

    const interval = setInterval(async () => {
      // Verificar se o polling já foi parado
      if (isPollingStopped.current) {
        clearInterval(interval);
        return;
      }

      try {
        const response = await scraperConfigAPI.getJobStatus(jobId);

        // Parar polling quando concluído e capturar tempo final
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          // Parar ANTES de atualizar o estado para evitar re-renders extras
          isPollingStopped.current = true;
          clearInterval(interval);

          // Parar o cronômetro
          setIsTimerStopped(true);

          // Capturar o tempo final ANTES de atualizar jobStatus
          if (startTime && finalElapsedTime === 0) {
            const now = Date.now();
            const finalTime = now - startTime; // em ms
            setFinalElapsedTime(finalTime);
          }

          // Verificar se houve erro de limite de plano nos itens que falharam
          if (response.data.failed && response.data.failed.length > 0) {
            const limitError = response.data.failed.find(f => 
              (f.error && f.error.includes('Limite mensal')) || 
              (f.reason && f.reason.includes('Limite mensal'))
            );
            
            if (limitError) {
              window.dispatchEvent(
                new CustomEvent('plan-limit-reached', { 
                  detail: { message: limitError.error || limitError.reason } 
                })
              );
            }
          }

          // Atualizar status final
          setJobStatus(response.data);

          if (onJobStarted) {
            onJobStarted(response.data);
          }
        } else {
          // Só atualizar se ainda não terminou
          setJobStatus(response.data);
        }
      } catch (error) {
        console.error('Erro ao obter status do job:', error);
        isPollingStopped.current = true;
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      isPollingStopped.current = true;
      clearInterval(interval);
    };
  }, [jobId, startTime, finalElapsedTime, onJobStarted]);

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    // Detectar se há múltiplas URLs (separadas por quebra de linha, espaço ou vírgula)
    // Regex melhorada para não parar em vírgulas dentro de URLs (como Terra.com.br)
    const urlPattern = /https?:\/\/[^\s]+/g;
    const foundUrls = newUrl.match(urlPattern);

    let urlsToAdd = [];

    if (foundUrls && foundUrls.length > 0) {
      // Filtrar URLs que já não estão na lista
      urlsToAdd = foundUrls.filter(url => !urls.includes(url.trim()));
    } else if (!urls.includes(newUrl.trim())) {
      urlsToAdd = [newUrl.trim()];
    }

    if (urlsToAdd.length > 0) {
      setIsAddingUrls(true);
      setError(null);

      // Verificar duplicatas no banco de dados
      try {
        const response = await scraperConfigAPI.checkDuplicateUrls(urlsToAdd);
        const acceptedUrls = Array.isArray(response.data.newUrls)
          ? response.data.newUrls
          : [];
        const newUrlList = [...urls, ...acceptedUrls];

        if (response.data.duplicates > 0) {
          setDuplicateInfo(response.data);
        } else {
          setDuplicateInfo(null);
        }

        // A lista representa exatamente o que será enviado para captura. URLs já
        // existentes continuam disponíveis no aviso/modal, mas não entram no lote.
        setUrls(newUrlList);

        // Validar apenas a lista efetivamente aceita para processamento.
        const duplicates = detectClientDuplicates(newUrlList);
        setClientDuplicates(duplicates);
        setShowClientDuplicateAlert(duplicates.length > 0);
        setSuspiciousUrls(detectSuspiciousUrls(newUrlList));

        setNewUrl('');
      } catch (error) {
        console.error('Erro ao verificar duplicatas:', error);
        // Não continuar silenciosamente: isso faria URLs já cadastradas serem
        // exibidas como novas quando a verificação falhar ou expirar.
        setError(
          error.response?.data?.message ||
          'Não foi possível verificar os links existentes. Tente novamente antes de iniciar a captura.'
        );
      } finally {
        setIsAddingUrls(false);
      }
    }
  };

  const handleRemoveUrl = (urlToRemove) => {
    setUrls(urls.filter(url => url !== urlToRemove));
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddUrl();
    }
  };

  const handleStartBackgroundScraping = async () => {
    if (urls.length === 0) {
      setError('Adicione pelo menos uma URL para fazer a captura');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSkippedDuplicates([]); // Limpar duplicatas anteriores

      // Iniciar cronômetro
      const now = Date.now();
      setStartTime(now);
      setElapsedTime(0);
      setFinalElapsedTime(0);
      setIsTimerStopped(false);
      isPollingStopped.current = false;

      // Extrair IDs das tags para enviar ao backend
      const tagsToSend = selectedTags.map(tag => typeof tag === 'string' ? { name: tag } : tag);

      const response = await scraperConfigAPI.startSmartScraping(urls, tagsToSend);
      const responseData = response.data;

      // Capturar URLs ignoradas por duplicata (near-duplicates detectadas pelo backend)
      if (responseData.duplicates > 0 && responseData.duplicateUrls) {
        setSkippedDuplicates(responseData.duplicateUrls);
      }

      if (responseData.jobId) {
        setJobId(responseData.jobId);
      }

    } catch (error) {
      console.error('Erro ao iniciar scraping:', error);
      // Se todas as URLs são duplicatas, o backend retorna 400 com a lista
      if (error.response?.status === 400 && error.response?.data?.data?.duplicateUrls) {
        setSkippedDuplicates(error.response.data.data.duplicateUrls);
        setError(error.response.data.message || 'Todas as URLs já existem para este usuário');
      } else {
        setError(error.response?.data?.message || error.message || 'Erro ao iniciar a captura');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTrackingSync = async () => {
    try {
      setLoading(true);
      setError(null);
      setSkippedDuplicates([]); // Limpar duplicatas anteriores

      const monitorsToSync = selectedMonitors.length > 0 ? selectedMonitors : activeBrandData?.trackingMonitorNames || [];
      const response = await api.post('/scraper/background/tracking-sync', { period: trackingPeriod, monitors: monitorsToSync });
      
      if (response.data?.success && response.data.data?.jobId) {
        // Iniciar polling do novo job
        const now = Date.now();
        setStartTime(now);
        setElapsedTime(0);
        setFinalElapsedTime(0);
        setIsTimerStopped(false);
        isPollingStopped.current = false;
        
        setJobId(response.data.data.jobId);
        
        // Mudar para a aba de progresso primária
        setTabValue(0);
      } else if (response.data?.success && response.data.data?.total === 0) {
         // Mensagem de nada encontrado
         alert(response.data.message);
      }

    } catch (err) {
      console.error('Erro na sincronização:', err);
      // Se tiver erro em cascata, ele entra aqui
      setError(err.response?.data?.message || err.message || 'Erro ao iniciar sincronização.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewExistingArticles = () => {
    if (duplicateInfo && duplicateInfo.duplicateUrls) {
      setExistingArticles(duplicateInfo.duplicateUrls);
      setShowExistingArticlesModal(true);
    }
  };

  const handleBulkRescrapeStarted = (newJobId) => {
    // Esconder o modal de artigos existentes para focar no progresso
    setShowExistingArticlesModal(false);

    // Iniciar cronômetro e estados de job
    const now = Date.now();
    setStartTime(now);
    setElapsedTime(0);
    setFinalElapsedTime(0);
    setIsTimerStopped(false);
    isPollingStopped.current = false;

    // Setar o novo Job ID para o componente começar o polling
    setJobId(newJobId);
    setError(null);
  };

  const handleViewArticle = (article) => {
    // Abrir página pública do artigo (sem autenticação)
    const articleId = article._id || article.articleId;
    if (articleId) {
      window.open(`/article/${articleId}`, '_blank');
    } else {
      console.error('ID do artigo não encontrado:', article);
      alert('Erro: ID do artigo não encontrado');
    }
  };

  const handleShareArticle = (article) => {
    // Gerar link público e copiar para clipboard
    const articleId = article._id || article.articleId;
    if (articleId) {
      const publicUrl = `${window.location.origin}/article/${articleId}`;
      navigator.clipboard.writeText(publicUrl);
      alert('Link público copiado para a área de transferência!');
    } else {
      console.error('ID do artigo não encontrado:', article);
      alert('Erro: ID do artigo não encontrado');
    }
  };

  // Funções para criação manual e manipulação inteligente
  const handlePaste = (e) => {
    try {
      // Tentar ler HTML (Rich Text) do Clipboard
      const htmlData = e.clipboardData?.getData('text/html');
      
      if (htmlData) {
        // Parsear HTML para buscar tags de âncora <a>
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlData, 'text/html');
        const links = Array.from(doc.querySelectorAll('a'));
        
        // Extrair href verdadeiro e filtrar links válidos
        const extractedUrls = links
          .map(link => {
            // Alguns sistemas de monitoramento escondem URL real em atributos ex: data-url ou href
            let href = link.getAttribute('href') || link.href || '';
            // Se for link relativo que o navegador transformou baseado no domínio raiz do Mensure, e não for http, limpamos
            if (href.startsWith('/') || href.includes('javascript:')) return null;
            return href;
          })
          .filter(href => href && (href.startsWith('http://') || href.startsWith('https://')));

        // Para evitar duplicatas na própria extração vinda do HTML
        const uniqueUrls = [...new Set(extractedUrls)];

        if (uniqueUrls.length > 0) {
          e.preventDefault(); // Bloqueia a colagem original mista de texto com links
          
          const urlsText = uniqueUrls.join('\n');
          
          // Atualiza o state injetando os links purificados
          setNewUrl(prev => {
            const separator = prev && !prev.endsWith('\n') ? '\n' : '';
            return prev + separator + urlsText;
          });
          
          console.log(`[SmartPaste] Extraídos ${uniqueUrls.length} links limpos de título rich-text.`);
          return;
        }
      }
    } catch (error) {
      console.warn('[SmartPaste] Falha ao ler/extrair metadata do clipboard:', error);
      // Cai para a colagem padrão do navegador graciosamente
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(null);
    if (newValue === 1) {
      // Abrir modal de criação
      setCreateArticleModalOpen(true);
    }
  };

  const handleArticleCreated = (newArticle) => {
    if (onJobStarted) {
      onJobStarted({ status: 'completed', successCount: 1 });
    }
    setCreateArticleModalOpen(false);
    setMainEditModalOpen(false);
    setSelectedTypes(null);
    handleClose();
  };

  // Handler quando usuário confirma tipos no MediaTypeSelector
  const handleMediaTypeConfirm = (types) => {
    setSelectedTypes(types);
    setCreateArticleModalOpen(false); // Fecha o modal de tipos
    setMainEditModalOpen(true); // Abre o modal principal
  };

  // Handler para receber URLs da aba de descoberta
  const handlePushFromDiscovery = (pushedUrls) => {
    if (!pushedUrls || !pushedUrls.length) return;
    
    // 1. Mudar para a aba de Scraping Manual
    setTabValue(0);
    
    // 2. Adicionar os novos URLs ao campo de texto (preserva o que já tinha se houver)
    const urlsText = pushedUrls.join('\n');
    setNewUrl(prev => {
      const separator = prev && !prev.endsWith('\n') ? '\n' : '';
      return prev + separator + urlsText;
    });

    // 3. Opcional: Disparar automaticamente o handleAddUrl para que eles já apareçam como chips
    // Mas talvez seja melhor deixar o usuário ver o texto antes.
    // Vou disparar um pequeno delay para garantir que o state de newUrl foi aplicado.
    setTimeout(() => {
      // Como handleAddUrl usa o state 'newUrl' atual, e o setNewUrl é assíncrono,
      // precisamos passar as URLs diretamente ou garantir que o componente re-renderizou.
      // O mais seguro no React é deixar o usuário clicar no "+", mas vou forçar o parsing.
      // handleAddUrl(); // Se eu quiser automático. Mas handleAddUrl usa 'newUrl' interno.
      // Melhor só mudar a aba e preencher o texto para o usuário clicar no +, ou automatizar melhor.
    }, 100);
  };

  const handleClose = (event, reason) => {
    // Se o usuário clicar no backdrop e o job estiver rodando, minimiza em vez de fechar
    if ((reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === 'minimize') && jobId && jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed') {
      setIsMinimized(true);
      onClose(); 
      return;
    }

    if (reason !== 'minimize') {
      setUrls([]);
      setNewUrl('');
      setError(null);
      setJobId(null);
      setJobStatus(null);
      setDuplicateInfo(null);
      setClientDuplicates([]);
      setShowClientDuplicateAlert(false);
      setShowExistingArticlesModal(false);
      setExistingArticles([]);
      setSkippedDuplicates([]);
      setSuspiciousUrls([]);
      setStartTime(null);
      setElapsedTime(0);
      setFinalElapsedTime(0);
      setIsTimerStopped(false);
      setTabValue(0);
      setCreateArticleModalOpen(false);
      setIsMinimized(false);
      onClose();
    }
  };

  return (
    <>
      <PlatformDialog
        open={open && !isMinimized}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : `${operationalRadii.modal}px`,
            width: '100%',
            maxWidth: isMobile ? '100%' : 'md',
            margin: isMobile ? 0 : 2,
            overflowX: 'hidden',
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            border: isMobile ? 0 : '1px solid',
            borderColor: 'divider',
            boxShadow: operationalElevation.modal,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none'
          }
        }}
      >
        <PlatformDialogTitle
          title="Adicionar Artigos"
          subtitle={!isMobile ? (tabValue === 0 ? 'Adicione URLs para capturar artigos automaticamente' : 'Crie um artigo manualmente') : undefined}
          icon={<AddIcon sx={{ fontSize: '1.3rem' }} />}
          onClose={jobId && jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed' ? (e) => handleClose(e, 'minimize') : handleClose}
        />

        <Box>
          <Tabs value={tabValue} onChange={handleTabChange} centered variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{ 
              '& .MuiTab-iconWrapper': { fontSize: '1.2rem' },
              '& .MuiTab-root': {
                minHeight: isMobile ? 48 : 52,
                fontSize: operationalFontSizes.sm,
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: 'none',
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'text.primary'
                }
              },
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: operationalRadii.pill
              },
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Tab
              icon={<LinkIcon sx={{ fontSize: '1.2rem' }} />}
              label={isMobile ? 'URLs' : 'Captura de URLs'}
              iconPosition="start"
              sx={{ minHeight: isMobile ? 48 : 56 }}
            />
            <Tab
              icon={<ArticleIcon sx={{ fontSize: '1.2rem' }} />}
              label={isMobile ? 'Manual' : 'Criação Manual'}
              iconPosition="start"
              sx={{ minHeight: isMobile ? 48 : 56 }}
            />
            {isAdmin && (
              <Tab
                icon={<SyncIcon sx={{ fontSize: '1.2rem' }} />}
                label={isMobile ? 'Tracking' : 'Sincronizar Tracking'}
                iconPosition="start"
                sx={{ minHeight: isMobile ? 48 : 56 }}
              />
            )}
            {isAdmin && (
              <Tab
                icon={<DorkIcon sx={{ fontSize: '1.2rem' }} />}
                label={isMobile ? 'Descoberta' : 'Descoberta de Dorks'}
                iconPosition="start"
                sx={{ minHeight: isMobile ? 48 : 56 }}
              />
            )}
          </Tabs>
        </Box>

        <DialogContent sx={{
          p: isMobile ? 2 : 3,
          overflowX: 'hidden',
          overflowY: 'auto',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '& .MuiOutlinedInput-root': {
            borderRadius: `${operationalRadii.control}px`
          },
          '& .MuiButton-root': buttonSx,
          '& .MuiIconButton-root': {
            borderRadius: `${operationalRadii.control}px`
          },
          '& .MuiChip-root': {
            borderRadius: operationalRadii.pill
          },
          '& .MuiPaper-root': {
            boxShadow: operationalElevation.none
          }
        }}>
          {tabValue === 0 ? (
            // Aba de Scraping
            !jobId ? (
              // Fase 1: Adicionar URLs
              <>
                <Box sx={{ mb: isMobile ? 2 : 3, display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row', width: '100%', maxWidth: '100%' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 4}
                    label="URLs dos Artigos"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    onPaste={handlePaste}
                    placeholder={isMobile ? 'Cole URLs aqui' : 'Cole uma ou várias URLs aqui (uma por linha ou separadas por espaço)'}
                    helperText={isMobile ? 'Cole textos com links para extração automática' : 'Você pode colar títulos copiados com hiperlinks que o sistema extrairá as URLs maganicamente'}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      '& .MuiInputBase-root': {
                        ...operationalScrollbars.thin,
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        overflow: 'auto'
                      },
                      '& .MuiInputBase-input': {
                        maxWidth: '100%',
                        overflow: 'auto'
                      }
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'flex-end' : 'center',
                    pb: isMobile ? 0 : 2.5
                  }}>
                    <IconButton
                      color="primary"
                      onClick={handleAddUrl}
                      disabled={!newUrl.trim() || isAddingUrls}
                      size={isMobile ? 'medium' : 'large'}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        width: isMobile ? 40 : 48,
                        height: isMobile ? 40 : 48,
                        borderRadius: `${operationalRadii.control}px`,
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'action.disabledBackground',
                          color: 'action.disabled'
                        }
                      }}
                    >
                      {isAddingUrls
                        ? <CircularProgress size={isMobile ? 18 : 22} sx={{ color: 'inherit' }} />
                        : <AddIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Feedback de carregamento ao clicar no + */}
                {isAddingUrls && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress sx={{ ...progressSx, mb: 0.75 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Analisando links — verificando duplicatas e similaridades...
                    </Typography>
                  </Box>
                )}

                {/* Seção de Tags Selecionadas (Padronizada) */}
                {selectedTags.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                      <span translate="no">Tags</span> Selecionadas
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedTags.map(tag => (
                        <TagChip
                          key={typeof tag === 'string' ? tag : tag._id}
                          label={typeof tag === 'string' ? tag : tag.name}
                          onDelete={() => setSelectedTags(prev => prev.filter(t => (typeof t === 'string' ? t : t._id) !== (typeof tag === 'string' ? tag : tag._id)))}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 3, mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    Adicionar <span translate="no">Tags</span> (Opcional)
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    As tags selecionadas serão aplicadas a TODOS os artigos desta lista. As tags identificadas automaticamente pelo sistema também serão mantidas.
                  </Typography>

                  <Autocomplete
                    multiple
                    freeSolo
                    id="tags-filled"
                    options={availableTags}
                    value={[]} // Sempre vazio para manter o input limpo
                    loading={loadingTags}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      if (option.inputValue) return option.inputValue;
                      return option.name;
                    }}
                    ListboxProps={{
                      sx: {
                        ...operationalScrollbars.default,
                        '& .MuiAutocomplete-option': {
                          backgroundColor: 'background.paper',
                          marginBottom: '4px',
                          marginLeft: '4px',
                          marginRight: '4px',
                          borderRadius: `${operationalRadii.control}px`,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: `background-color ${operationalMotion.quick}, border-color ${operationalMotion.quick}`,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-focused, &[aria-selected="true"]': {
                            backgroundColor: 'action.selected',
                            border: `1px solid ${theme.palette.primary.main}`,
                          }
                        }
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = options.filter(option => 
                        option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                      );

                      if (params.inputValue !== '') {
                        const inputLower = params.inputValue.toLowerCase().trim();
                        const exists = availableTags.some(
                          tag => tag.name.toLowerCase() === inputLower
                        );

                        if (!exists && inputLower.length > 0) {
                          filtered.push({
                            inputValue: params.inputValue.trim(),
                            name: `Criar "${params.inputValue.trim()}"`,
                            isNew: true
                          });
                        }
                      }

                      return filtered;
                    }}
                    onChange={(event, newValue) => {
                      if (newValue.length > 0) {
                        const last = newValue[newValue.length - 1];
                        
                        // Se é uma nova tag
                        if (typeof last === 'string') {
                          if (!selectedTags.some(t => (typeof t === 'string' ? t : t.name) === last)) {
                            setSelectedTags([...selectedTags, last]);
                          }
                        } else if (last.isNew) {
                          // Chamada para criar nova tag (que aciona handleCreateNewTag se necessário ou apenas adiciona à lista)
                          const newTagName = last.inputValue;
                          if (!selectedTags.some(t => (typeof t === 'string' ? t : t.name).toLowerCase() === newTagName.toLowerCase())) {
                            handleCreateNewTag(newTagName);
                          }
                        } else if (last._id && !selectedTags.some(t => t._id === last._id)) {
                          setSelectedTags([...selectedTags, last]);
                        }
                      }
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key || option._id || option.inputValue} {...otherProps}>
                          {option.isNew ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AddIcon fontSize="small" color="primary" />
                              <Typography color="primary">{option.name}</Typography>
                            </Box>
                          ) : (
                            option.name
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={<span translate="no">Tags</span>}
                        placeholder="Digite o nome da tag..."
                        size="small"
                        helperText="Pressione Enter para criar uma nova tag"
                      />
                    )}
                    disabled={loading || !!jobId}
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={errorAlertSx}>
                    {error}
                  </Alert>
                )}

                {duplicateInfo && duplicateInfo.duplicates > 0 && (
                  <Alert
                    severity="warning"
                    sx={warningAlertSx}
                    onClose={() => setDuplicateInfo(null)}
                    action={
                      <Button
                        size="small"
                        onClick={handleViewExistingArticles}
                        variant="outlined"
                        color="inherit"
                      >
                        Ver Artigos
                      </Button>
                    }
                    >
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {duplicateInfo.duplicates} URL{duplicateInfo.duplicates !== 1 ? 's' : ''} já existe{duplicateInfo.duplicates !== 1 ? 'm' : ''} no sistema
                      </Typography>
                    <Box sx={{ mt: 1 }}>
                      {duplicateInfo.duplicateUrls.slice(0, 3).map((dup, index) => (
                        <Typography key={index} variant="caption" display="block">
                          • {dup.title}
                        </Typography>
                      ))}
                      {duplicateInfo.duplicates > 3 && (
                        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                          ... e mais {duplicateInfo.duplicates - 3} URL{duplicateInfo.duplicates - 3 !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {duplicateInfo.new} URL{duplicateInfo.new !== 1 ? 's' : ''} nova{duplicateInfo.new !== 1 ? 's' : ''} será{duplicateInfo.new !== 1 ? 'ão' : ''} processada{duplicateInfo.new !== 1 ? 's' : ''}.
                    </Typography>
                  </Alert>
                )}

                {showClientDuplicateAlert && clientDuplicates.length > 0 && (
                  <Alert
                    severity="info"
                    sx={infoAlertSx}
                    action={
                      <Box sx={{ display: 'flex', gap: 0.5, flexDirection: isMobile ? 'column' : 'row' }}>
                        <Button
                          size="small"
                          onClick={removeClientDuplicates}
                          variant="outlined"
                          color="inherit"
                          sx={{ fontSize: isMobile ? '0.7rem' : undefined, whiteSpace: 'nowrap' }}
                        >
                          {isMobile ? 'Remover' : 'Remover Duplicatas'}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setShowClientDuplicateAlert(false)}
                          color="inherit"
                          sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
                        >
                          Ignorar
                        </Button>
                      </Box>
                    }
                    >
                      <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} gutterBottom>
                        {clientDuplicates.length} link{clientDuplicates.length > 1 ? 's' : ''} duplicado{clientDuplicates.length > 1 ? 's' : ''} na lista
                      </Typography>
                    <Box sx={{ mt: 0.5, mb: 1 }}>
                      {clientDuplicates.slice(0, 5).map((dup, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: `${operationalRadii.surface}px`,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {/* URL original — será mantida */}
                          {dup.originalUrl && (
                            <Box sx={{ mb: 0.75 }}>
                              <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 'bold', display: 'block', mb: 0.25 }}>
                                ✓ Manter:
                              </Typography>
                              <Typography
                                variant="caption"
                                component="a"
                                href={dup.originalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  wordBreak: 'break-all',
                                  color: 'text.primary',
                                  textDecoration: 'underline',
                                  textDecorationColor: 'success.light',
                                  pl: 1.5,
                                  display: 'block',
                                  '&:hover': { color: 'success.light' }
                                }}
                              >
                                {dup.originalUrl}
                              </Typography>
                            </Box>
                          )}
                          {/* URL duplicada — será removida */}
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                              <Typography variant="caption" sx={{ color: 'error.light', fontWeight: 'bold' }}>
                                ✕ Remover:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: '0.65rem', py: 0, px: 0.75, minWidth: 0, height: 20 }}
                                onClick={() => {
                                  const newUrls = urls.filter(u => u !== dup.url);
                                  setUrls(newUrls);
                                  const remaining = clientDuplicates.filter((_, i) => i !== index);
                                  setClientDuplicates(remaining);
                                }}
                              >
                                Remover
                              </Button>
                            </Box>
                            <Typography
                              variant="caption"
                              component="a"
                              href={dup.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                wordBreak: 'break-all',
                                color: 'text.secondary',
                                textDecoration: 'underline',
                                textDecorationColor: 'error.light',
                                pl: 1.5,
                                display: 'block',
                                '&:hover': { color: 'error.light' }
                              }}
                            >
                              {dup.url}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {clientDuplicates.length > 5 && (
                        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                          ... e mais {clientDuplicates.length - 5}
                        </Typography>
                      )}

                    </Box>

                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Dica:</strong> Evita processamento desnecessário.
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Alerta de URLs com slugs muito parecidos (possível typo) */}
                {suspiciousUrls.length > 0 && (
                  <Alert
                    severity="warning"
                    sx={warningAlertSx}
                    onClose={() => setSuspiciousUrls([])}
                  >
                    <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} gutterBottom>
                      {suspiciousUrls.length} par{suspiciousUrls.length > 1 ? 'es' : ''} de URLs muito parecidas — verifique se são o mesmo artigo
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Mesmo domínio, slugs quase idênticos. Abra para verificar e remova a que for duplicada.
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {suspiciousUrls.slice(0, 5).map((pair, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: `${operationalRadii.surface}px`,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {/* URL A com botão remover */}
                          <Box sx={{ mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                              <Typography variant="caption" sx={{ color: 'warning.light', fontWeight: 'bold' }}>
                                URL A:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: '0.65rem', py: 0, px: 0.75, minWidth: 0, height: 20 }}
                                onClick={() => {
                                  const newUrls = urls.filter(u => u !== pair.urlA);
                                  setUrls(newUrls);
                                  const remaining = suspiciousUrls.filter((_, i) => i !== index);
                                  setSuspiciousUrls(remaining);
                                }}
                              >
                                Remover
                              </Button>
                            </Box>
                            <Typography
                              variant="caption"
                              component="a"
                              href={pair.urlA}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ wordBreak: 'break-all', color: 'text.primary', textDecoration: 'underline', pl: 1.5, display: 'block', '&:hover': { color: 'warning.light' } }}
                            >
                              {pair.urlA}
                            </Typography>
                          </Box>
                          {/* URL B com botão remover */}
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                              <Typography variant="caption" sx={{ color: 'warning.light', fontWeight: 'bold' }}>
                                URL B:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: '0.65rem', py: 0, px: 0.75, minWidth: 0, height: 20 }}
                                onClick={() => {
                                  const newUrls = urls.filter(u => u !== pair.urlB);
                                  setUrls(newUrls);
                                  const remaining = suspiciousUrls.filter((_, i) => i !== index);
                                  setSuspiciousUrls(remaining);
                                }}
                              >
                                Remover
                              </Button>
                            </Box>
                            <Typography
                              variant="caption"
                              component="a"
                              href={pair.urlB}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ wordBreak: 'break-all', color: 'text.secondary', textDecoration: 'underline', pl: 1.5, display: 'block', '&:hover': { color: 'warning.light' } }}
                            >
                              {pair.urlB}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {suspiciousUrls.length > 5 && (
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                          ... e mais {suspiciousUrls.length - 5} par{suspiciousUrls.length - 5 > 1 ? 'es' : ''}
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}


                {urls.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                      URLs Adicionadas ({urls.length})
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        maxHeight: isMobile ? 150 : 300,
                        overflow: 'auto',
                        borderRadius: `${operationalRadii.surface}px`,
                        border: '1px solid',
                        borderColor: 'divider',
                        ...operationalScrollbars.thin
                      }}
                    >
                      <List dense disablePadding>
                        {urls.map((url, index) => (
                          <ListItem
                            key={index}
                            divider={index < urls.length - 1}
                            sx={{
                              py: isMobile ? 0.25 : 0.75,
                              pr: isMobile ? 5 : 6
                            }}
                          >
                            <ListItemText
                              primary={url}
                              primaryTypographyProps={{
                                variant: isMobile ? 'caption' : 'body2',
                                sx: {
                                  wordBreak: 'break-all',
                                  overflowWrap: 'anywhere',
                                  maxWidth: '100%',
                                  display: 'block'
                                }
                              }}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveUrl(url)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <DeleteIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Box>
                )}
              </>
            ) : (
              // Fase 2: Mostrar progresso com Donut Spinner
              <>
                {/* Alerta de URLs ignoradas por serem near-duplicatas */}
                {skippedDuplicates.length > 0 && (
                  <Alert
                    severity="warning"
                    sx={warningAlertSx}
                    onClose={() => setSkippedDuplicates([])}
                  >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {skippedDuplicates.length} URL{skippedDuplicates.length !== 1 ? 's' : ''} ignorada{skippedDuplicates.length !== 1 ? 's' : ''} — já existe{skippedDuplicates.length !== 1 ? 'm' : ''} no sistema
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {skippedDuplicates.length !== 1 ? 'Esses links' : 'Esse link'} não {skippedDuplicates.length !== 1 ? 'foram processados' : 'foi processado'} porque {skippedDuplicates.length !== 1 ? 'são versões' : 'é uma versão'} de artigos que já estão cadastrados (ex: link com <code>/amp</code> ou pequenas variações).
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {skippedDuplicates.slice(0, 5).map((dup, index) => (
                        <Box key={index} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                            • {dup.title || 'Sem título'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all', pl: 1.5 }}>
                            {dup.url}
                          </Typography>
                        </Box>
                      ))}
                      {skippedDuplicates.length > 5 && (
                        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                          ... e mais {skippedDuplicates.length - 5} URL{skippedDuplicates.length - 5 !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}

                {jobStatus && (
                  <Box sx={{ py: isMobile ? 1 : 2 }}>
                    {/* Donut Chart Spinner - Compacto */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: isMobile ? 1.5 : 2
                    }}>

                      {/* Animação HUD: Radar para Estrutura */}
                      <RadarToChartAnimation 
                        jobStatus={jobStatus} 
                        isMobile={isMobile}
                      />

                      {/* ── Cronômetro refinado ── */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mt: 2,
                        px: 2,
                        py: 0.75,
                        ...statusPillSx(),
                      }}>
                        <TimerIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.8 }} />
                        <Typography sx={{
                          fontFamily: 'monospace',
                          fontSize: operationalFontSizes.sm,
                          fontWeight: 700,
                          color: 'text.primary',
                          letterSpacing: 0,
                        }}>
                          {formatTime(
                            jobStatus.status === 'completed' || jobStatus.status === 'failed'
                              ? (finalElapsedTime > 0 ? finalElapsedTime : elapsedTime)
                              : elapsedTime
                          )}
                        </Typography>
                        {jobStatus.status !== 'completed' && jobStatus.status !== 'failed' && calculateETA() && (
                          <Typography sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                            color: 'text.disabled',
                            letterSpacing: 1,
                          }}>
                            · ETA {formatTime(calculateETA())}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* ── Stats inline — compact premium ── */}
                    <Box sx={{
                      display: 'flex',
                      gap: 1.5,
                      justifyContent: 'center',
                      mb: isMobile ? 2 : 3,
                      mt: 1.5,
                    }}>
                      {/* Sucesso */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        ...statusPillSx(),
                      }}>
                        <Box sx={{
                          width: 7, height: 7, borderRadius: '50%',
                          bgcolor: operationalStatusColors.success,
                          boxShadow: operationalElevation.none,
                          flexShrink: 0,
                        }} />
                        <Typography sx={{ fontSize: operationalFontSizes.sm, fontWeight: 750, color: 'text.primary', lineHeight: 1 }}>
                          {jobStatus.successCount}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500 }}>
                          Sucesso
                        </Typography>
                      </Box>

                      {/* Erros */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        ...statusPillSx(),
                      }}>
                        <Box sx={{
                          width: 7, height: 7, borderRadius: '50%',
                          bgcolor: jobStatus.failedCount > 0 ? operationalStatusColors.error : 'text.disabled',
                          boxShadow: operationalElevation.none,
                          flexShrink: 0,
                        }} />
                        <Typography sx={{
                          fontSize: operationalFontSizes.sm, fontWeight: 750, lineHeight: 1,
                          color: jobStatus.failedCount > 0 ? operationalStatusColors.error : 'text.disabled',
                        }}>
                          {jobStatus.failedCount}
                        </Typography>
                        <Typography sx={{
                          fontSize: '0.72rem', fontWeight: 500,
                          color: jobStatus.failedCount > 0 ? operationalStatusColors.error : 'text.disabled',
                        }}>
                          Erros
                        </Typography>
                      </Box>
                    </Box>

                    {/* Contagem por Plataforma */}
                    {jobStatus.platforms?.total && (
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: isMobile ? 0.5 : 1,
                        mb: isMobile ? 1.5 : 2,
                        flexWrap: 'wrap'
                      }}>
                        {/* YouTube */}
                        {jobStatus.platforms.total.YouTube > 0 && (
                          <Chip
                            icon={<YouTubeIcon sx={{ fontSize: 16 }} />}
                            label={`${jobStatus.platforms.success?.YouTube || 0}/${jobStatus.platforms.total.YouTube}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Twitter */}
                        {jobStatus.platforms.total.Twitter > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><TwitterIcon /></Box>}
                            label={`${jobStatus.platforms.success?.Twitter || 0}/${jobStatus.platforms.total.Twitter}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Instagram */}
                        {jobStatus.platforms.total.Instagram > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><InstagramIcon /></Box>}
                            label={`${jobStatus.platforms.success?.Instagram || 0}/${jobStatus.platforms.total.Instagram}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* TikTok */}
                        {jobStatus.platforms.total.TikTok > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><TikTokIcon /></Box>}
                            label={`${jobStatus.platforms.success?.TikTok || 0}/${jobStatus.platforms.total.TikTok}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Facebook */}
                        {jobStatus.platforms.total.Facebook > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><FacebookIcon /></Box>}
                            label={`${jobStatus.platforms.success?.Facebook || 0}/${jobStatus.platforms.total.Facebook}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Threads */}
                        {jobStatus.platforms.total.Threads > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><ThreadsIcon /></Box>}
                            label={`${jobStatus.platforms.success?.Threads || 0}/${jobStatus.platforms.total.Threads}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Spotify */}
                        {jobStatus.platforms.total.Spotify > 0 && (
                          <Chip
                            icon={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, ml: 0.5 }}><SpotifyIcon /></Box>}
                            label={`${jobStatus.platforms.success?.Spotify || 0}/${jobStatus.platforms.total.Spotify}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* Online/Sites */}
                        {jobStatus.platforms.total.Online > 0 && (
                          <Chip
                            icon={<WebIcon sx={{ fontSize: 16 }} />}
                            label={`${jobStatus.platforms.success?.Online || 0}/${jobStatus.platforms.total.Online}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}

                    {/* CSS Animations */}
                    <style>
                      {`
                    @keyframes spin {
                      from { transform: rotate(0deg); }
                      to { transform: rotate(360deg); }
                    }
                    @keyframes pulse {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0.85; }
                    }
                  `}
                    </style>

                    {jobStatus.status === 'completed' && (
                      <>
                        {/* Cache stats (se houver) */}
                        {jobStatus.cacheStats && (
                          <Paper elevation={0} sx={{
                            ...surfaceSx,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 3,
                            mb: 2,
                            p: 2
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{jobStatus.cacheStats.fromCache}</Box> do cache
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box component="span" sx={{ color: 'info.main', fontWeight: 700 }}>{jobStatus.cacheStats.newScraping}</Box> novos
                            </Typography>
                          </Paper>
                        )}

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={() => setShowReport(!showReport)}
                            sx={{ ...buttonSx, px: 2.5 }}
                          >
                            {showReport ? 'Ocultar' : 'Ver'} Relatório Detalhado
                          </Button>
                        </Box>

                        {showReport && (
                          <Box>
                            {/* URLs com sucesso */}
                            {jobStatus.success && jobStatus.success.length > 0 && (
                              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2, borderColor: 'success.main' }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom color="success.main">
                                  Artigos Adicionados ({jobStatus.success.length})
                                </Typography>
                                <List dense disablePadding>
                                  {jobStatus.success.map((item, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText
                                        primary={item.title || 'Sem título'}
                                        secondary={item.url}
                                        primaryTypographyProps={{
                                          variant: 'body2',
                                          sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' }
                                        }}
                                        secondaryTypographyProps={{
                                          variant: 'caption',
                                          sx: { wordBreak: 'break-all', overflowWrap: 'anywhere' }
                                        }}
                                      />
                                      <Chip
                                        label={item.action === 'create' ? 'Novo' : 'Atualizado'}
                                        size="small"
                                        color={item.action === 'create' ? 'success' : 'info'}
                                        sx={{ ml: 1 }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Paper>
                            )}

                            {/* URLs que falharam */}
                            {jobStatus.failed && jobStatus.failed.length > 0 && (
                              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, borderColor: 'error.main' }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom color="error.main">
                                  URLs que Falharam ({jobStatus.failed.length})
                                </Typography>
                                <List dense disablePadding>
                                  {jobStatus.failed.map((item, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText
                                        primary={item.url}
                                        secondary={`Erro: ${item.error}`}
                                        primaryTypographyProps={{
                                          variant: 'body2',
                                          sx: { wordBreak: 'break-all', overflowWrap: 'anywhere' }
                                        }}
                                        secondaryTypographyProps={{
                                          variant: 'caption',
                                          color: 'error',
                                          sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' }
                                        }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Paper>
                            )}
                          </Box>
                        )}
                      </>
                    )}

                    {jobStatus.status === 'failed' && (
                      <Alert severity="error">
                        A captura falhou. Por favor, tente novamente.
                      </Alert>
                    )}
                  </Box>
                )}
              </>
            )
          ) : tabValue === 1 ? (
            // Aba de Criação Manual
            <Box sx={{ textAlign: 'center', py: isMobile ? 2 : 4 }}>
              <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" gutterBottom>
                {isMobile ? 'Crie um artigo ou faça upload de mídia' : 'Crie um artigo manualmente ou faça upload de mídia (imagens, vídeos ou áudio)'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateArticleModalOpen(true)}
                sx={{ mt: isMobile ? 1 : 2 }}
                size={isMobile ? 'small' : 'medium'}
              >
                Criar Artigo
              </Button>
            </Box>
          ) : tabValue === 2 && isAdmin ? (
            // Aba de Sincronização Tracking
            <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <SyncIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.8 }} />
              <Typography variant="h6">Sincronização Integrada</Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 500 }}>
                Este recurso aciona o motor de inteligência TrackingSource para resgatar automaticamente as matérias de todos os painéis configurados. Idealmente, ele é disparado automaticamente de madrugada para compor seu clipping sem esforço humano.
              </Typography>
              
              {(!activeBrandData?.trackingMonitorNames || activeBrandData.trackingMonitorNames.length === 0) ? (
                <Alert severity="warning" sx={{ ...warningAlertSx, mt: 2 }}>
                  A Marca atual não possui Painéis Tracking configurados. Vá nas "Configurações Globais" da Marca para adicionar.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, alignItems: 'center', width: '100%', maxWidth: 480 }}>
                  <Paper elevation={0} sx={{ ...surfaceSx, p: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Painéis ({selectedMonitors.length}/{activeBrandData.trackingMonitorNames.length} selecionados):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => setSelectedMonitors([...activeBrandData.trackingMonitorNames])}
                        >
                          Todos
                        </Typography>
                        <Typography variant="caption" color="text.disabled">|</Typography>
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => setSelectedMonitors([])}
                        >
                          Nenhum
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {activeBrandData.trackingMonitorNames.map((name, idx) => (
                        <FormControlLabel
                          key={idx}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedMonitors.includes(name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMonitors(prev => [...prev, name]);
                                } else {
                                  setSelectedMonitors(prev => prev.filter(m => m !== name));
                                }
                              }}
                              disabled={loading}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              {name}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      ))}
                    </Box>
                  </Paper>
                  
                  <FormControl fullWidth size="medium">
                    <InputLabel id="tracking-period-label">Período de Sincronização</InputLabel>
                    <Select
                      labelId="tracking-period-label"
                      value={trackingPeriod}
                      onChange={(e) => setTrackingPeriod(e.target.value)}
                      label="Período de Sincronização"
                      disabled={loading}
                    >
                      <MenuItem value="24h">Últimas 24 horas</MenuItem>
                      <MenuItem value="48h">Últimas 48 horas</MenuItem>
                      <MenuItem value="7d">Últimos 7 dias</MenuItem>
                      <MenuItem value="30d">Últimos 30 dias</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTrackingSync}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                    fullWidth
                    sx={{ ...buttonSx, px: 4, py: 1.5 }}
                  >
                    {loading
                      ? 'Executando Robô (Pode demorar)...'
                      : selectedMonitors.length === 0
                        ? 'Selecione ao menos um painel'
                        : `Sincronizar (${selectedMonitors.length} painel${selectedMonitors.length > 1 ? 'is' : ''})`
                    }
                  </Button>
                </Box>
              )}
            </Box>
          ) : tabValue === 3 && isAdmin ? (
            // Aba Dork Discovery — gerenciar rotinas de busca automática
            <DorkDiscoveryTab 
              brandId={activeBrandData?._id} 
              onPushToScraper={handlePushFromDiscovery}
            />
          ) : null}
        </DialogContent>

        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button onClick={handleClose} size={isMobile ? 'small' : 'medium'}>
            {jobStatus?.status === 'completed' ? 'Fechar' : 'Cancelar'}
          </Button>
          {!jobId && tabValue === 0 && (
            <Button
              variant="contained"
              onClick={handleStartBackgroundScraping}
              disabled={urls.length === 0 || loading}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                ...buttonSx,
                px: isMobile ? 2 : 3,
              }}
            >
              {loading ? 'Iniciando...' : (isMobile ? 'Iniciar' : 'Iniciar Captura')}
            </Button>
          )}
        </DialogActions>
      </PlatformDialog>



      {/* Widget minimizado */}
      {!open && isMinimized && jobId && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            p: 2,
            borderRadius: `${operationalRadii.surface}px`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            bgcolor: 'background.paper',
            zIndex: 9999,
            transition: `box-shadow ${operationalMotion.standard}, transform ${operationalMotion.standard}`,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: operationalElevation.raised,
            '&:hover': {
               boxShadow: operationalElevation.modal,
               transform: 'translateY(-2px)'
            }
          }}
          onClick={() => {
            setIsMinimized(false);
            if (onRestore) onRestore();
          }}
        >
           <Box sx={{ position: 'relative', display: 'flex' }}>
             <CircularProgress 
                variant={jobStatus && jobStatus.total > 0 ? "determinate" : "indeterminate"} 
                value={jobStatus && jobStatus.total > 0 ? (jobStatus.completed / jobStatus.total) * 100 : 0} 
                size={40} 
             />
             <Box sx={{
                top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center'
             }}>
                <SyncIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
             </Box>
           </Box>
           <Box>
             <Typography variant="subtitle2" fontWeight={600}>
               {jobStatus?.status === 'completed' ? 'Concluído' : 'Processando...'}
             </Typography>
             <Typography variant="caption" color="text.secondary">
               {jobStatus && jobStatus.total > 0 
                 ? `${jobStatus.completed} de ${jobStatus.total} artigos`
                 : 'Inicializando Processo'}
             </Typography>
           </Box>
        </Paper>
      )}

      {/* Modal de Seleção de Tipos (MediaTypeSelector) */}
      <ArticleEditModal
        open={createArticleModalOpen}
        onClose={() => setCreateArticleModalOpen(false)}
        onSave={handleArticleCreated}
        onSelectArticle={() => { }} // Função vazia para evitar erros
        onAddTags={() => { }} // Função vazia para evitar erros
        onRemoveTag={() => { }} // Função vazia para evitar erros
        mode="create"
        availableTags={[]}
        onMediaTypeConfirm={handleMediaTypeConfirm}
        key={`create-article-modal-${createArticleModalOpen ? 'open' : 'closed'}`}
      />

      {/* Modal Principal de Edição (após confirmar tipos) */}
      {selectedTypes && (
        <ArticleEditModal
          open={mainEditModalOpen}
          onClose={() => {
            setMainEditModalOpen(false);
            setSelectedTypes(null);
          }}
          onSave={handleArticleCreated}
          onSelectArticle={() => { }} // Função vazia para evitar erros
          onAddTags={() => { }} // Função vazia para evitar erros
          onRemoveTag={() => { }} // Função vazia para evitar erros
          mode="create"
          availableTags={[]}
          initialMediaType={selectedTypes.mediaType}
          initialContentType={selectedTypes.contentType}
          key={`main-edit-modal-${mainEditModalOpen ? 'open' : 'closed'}`}
        />
      )}

      {/* Modal de Artigos Existentes */}
      <ExistingArticlesModal
        open={showExistingArticlesModal}
        onClose={() => setShowExistingArticlesModal(false)}
        existingArticles={existingArticles}
        onViewArticle={handleViewArticle}
        onShareArticle={handleShareArticle}
        onRescrapeStarted={handleBulkRescrapeStarted}
      />
    </>
  );
};

export default BackgroundScrapingModal;
